import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * Georgia Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const GALicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 12,
      cycleYears: 1,
      terminology: 'LU',
      highlights: ['12 LU annually', 'Min 8 LU in Health, Safety & Welfare', '1 LU in Accessibility', 'Reportable by December 31 each year', 'Max 6 LU self-study'],
      stateBoard: 'Georgia Board of Architects',
      boardUrl: 'https://sos.ga.gov/page/architects'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['30 PDH biennial', 'Min 2 PDH in Ethics or Professionalism', 'Technical engineering subjects', 'Min 15 PDH per year', 'Carryover allowed up to 15 PDH'],
      stateBoard: 'Georgia Board of Professional Engineers & Land Surveyors',
      boardUrl: 'https://sos.ga.gov/page/engineers-land-surveyors'
    },
    {
      id: 'PLS',
      name: 'Professional Land Surveyor',
      icon: '📐',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['30 PDH biennial', 'Min 2 PDH in Ethics or Professionalism', 'Land surveying-related topics', 'Min 15 PDH per year', 'Carryover allowed up to 15 PDH'],
      stateBoard: 'Georgia Board of Professional Engineers & Land Surveyors',
      boardUrl: 'https://sos.ga.gov/page/engineers-land-surveyors'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 80,
      cycleYears: 2,
      terminology: 'CPE',
      highlights: ['80 CPE hours biennial', 'Min 20 hours annually', '4 hours Ethics (every 3 years)', 'Min 40% technical subjects', 'Reporting period aligned with license expiration'],
      stateBoard: 'Georgia State Board of Accountancy',
      boardUrl: 'https://sos.ga.gov/page/accountancy'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 0,
      cycleYears: 2,
      terminology: 'N/A',
      highlights: ['Biennial renewal', 'No CE requirement for renewal', 'Must maintain competency', 'Active practice or refresher course if inactive >5 years', 'Online renewal available'],
      stateBoard: 'Georgia Board of Nursing',
      boardUrl: 'https://sos.ga.gov/page/nursing'
    },
    {
      id: 'APRN',
      name: 'Advanced Practice Registered Nurse',
      icon: '🩺',
      credits: 0,
      cycleYears: 2,
      terminology: 'N/A',
      highlights: ['Biennial renewal', 'No CE requirement for renewal', 'Must maintain national certification', 'Active practice recommended', 'Separate license from RN'],
      stateBoard: 'Georgia Board of Nursing',
      boardUrl: 'https://sos.ga.gov/page/nursing'
    },
    {
      id: 'LCSW',
      name: 'Licensed Clinical Social Worker',
      icon: '🧠',
      credits: 35,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['35 hours biennial', '6 hours Ethics (mandatory)', '3 hours Domestic Violence', '3 hours Addictive Disorders', 'Clinical social work practice'],
      stateBoard: 'Georgia Composite Board of Professional Counselors, Social Workers, and Marriage & Family Therapists',
      boardUrl: 'https://sos.ga.gov/page/social-workers'
    },
    {
      id: 'LPC',
      name: 'Licensed Professional Counselor',
      icon: '💭',
      credits: 35,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['35 hours biennial', '6 hours Ethics (mandatory)', '3 hours Domestic Violence', '3 hours Addictive Disorders', 'Professional counseling topics'],
      stateBoard: 'Georgia Composite Board of Professional Counselors, Social Workers, and Marriage & Family Therapists',
      boardUrl: 'https://sos.ga.gov/page/professional-counselors'
    },
    {
      id: 'LMFT',
      name: 'Licensed Marriage & Family Therapist',
      icon: '👨‍👩‍👧‍👦',
      credits: 35,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['35 hours biennial', '6 hours Ethics (mandatory)', '3 hours Domestic Violence', '3 hours Addictive Disorders', 'Marriage and family therapy topics'],
      stateBoard: 'Georgia Composite Board of Professional Counselors, Social Workers, and Marriage & Family Therapists',
      boardUrl: 'https://sos.ga.gov/page/marriage-family-therapists'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker/Salesperson',
      icon: '🏠',
      credits: 36,
      cycleYears: 4,
      terminology: 'hours',
      highlights: ['36 hours every 4 years', '3 hours License Law (mandatory)', '6 hours Risk Management', '27 hours elective', 'Completion required before renewal'],
      stateBoard: 'Georgia Real Estate Commission',
      boardUrl: 'https://www.grec.state.ga.us/'
    },
    {
      id: 'APPR',
      name: 'Certified Real Estate Appraiser',
      icon: '📊',
      credits: 28,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['28 hours biennial (Certified)', '14 hours annually (Licensed)', '7-hour National USPAP Update (mandatory)', 'AQB-approved education', 'State-specific requirements'],
      stateBoard: 'Georgia Real Estate Appraisers Board',
      boardUrl: 'https://sos.ga.gov/page/real-estate-appraisers'
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 12,
      cycleYears: 1,
      terminology: 'CLE',
      highlights: ['12 CLE hours annually', '1 hour Legal Ethics (mandatory)', '1 hour Professionalism (mandatory)', 'Max 6 hours self-study', 'Calendar year basis (Jan 1 - Dec 31)'],
      stateBoard: 'State Bar of Georgia - CLE',
      boardUrl: 'https://www.gabar.org/forthepublic/cle.cfm'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Georgia Professional License Renewal Requirements",
      "description": "Complete guide to Georgia professional license renewal requirements for Architects, Engineers, CPAs, Nurses, Social Workers, and more.",
      "url": "https://cectrack.com/ga-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://sos.ga.gov/",
          "https://www.gabar.org/"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Georgia Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": prof.credits > 0 
              ? `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`
              : `${prof.name} license renewal - maintain active status`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Georgia License Renewal",
            "item": "https://cectrack.com/ga-license-renewal"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my Georgia professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in Georgia. Architects need 12 LU annually, Engineers need 30 PDH biennial, CPAs need 80 CPE hours biennial, Social Workers and Therapists need 35 hours biennial, Real Estate professionals need 36 hours every 4 years, and Attorneys need 12 CLE hours annually. Nurses have no formal CE requirement."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in Georgia?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Georgia Secretary of State oversees most professional licensing boards including Architects, Engineers, CPAs, Nurses, Social Workers, Counselors, Therapists, and Appraisers. The Georgia Real Estate Commission regulates real estate professionals, and the State Bar of Georgia administers attorney CLE requirements."
          }
        },
        {
          "@type": "Question",
          "name": "What are the mandatory CE topics for Georgia professionals?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Many Georgia professions have mandatory CE topics: Architects need 8 LU in HSW and 1 LU in Accessibility; Engineers need 2 PDH in Ethics; CPAs need 4 hours in Ethics every 3 years; Social Workers, Counselors, and Therapists need 6 hours Ethics, 3 hours Domestic Violence, and 3 hours Addictive Disorders; Real Estate professionals need 3 hours License Law and 6 hours Risk Management; Attorneys need 1 hour Legal Ethics and 1 hour Professionalism."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my Georgia professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Georgia renewal periods vary by profession: Architects and Attorneys renew annually; Engineers, CPAs, Nurses, Social Workers, Counselors, Therapists, and Appraisers renew biennially (every 2 years); Real Estate professionals renew every 4 years."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Georgia Secretary of State - Professional Licensing Boards Division",
      "url": "https://sos.ga.gov/",
      "telephone": "+1-844-753-7825",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "237 Coliseum Drive",
        "addressLocality": "Macon",
        "addressRegion": "GA",
        "postalCode": "31217",
        "addressCountry": "US"
      },
      "areaServed": "Georgia",
      "description": "Oversees professional licensing for Architects, Engineers, CPAs, Nurses, Social Workers, Counselors, Therapists, and Appraisers in Georgia"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "State Bar of Georgia",
      "url": "https://www.gabar.org/",
      "telephone": "+1-404-527-8700",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "104 Marietta Street NW, Suite 100",
        "addressLocality": "Atlanta",
        "addressRegion": "GA",
        "postalCode": "30303",
        "addressCountry": "US"
      },
      "areaServed": "Georgia",
      "description": "Administers Continuing Legal Education (CLE) requirements for attorneys licensed in Georgia"
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Georgia Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Georgia professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Nurses, Social Workers, Attorneys, and more."
        keywords="Georgia license renewal, GA CE requirements, GA continuing education, Georgia professional licenses, Georgia Secretary of State, Georgia nurses, GA CPAs, GA engineers, GA attorneys"
        canonical="https://cectrack.com/ga-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #ea580c 0%, #fb923c 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Georgia Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Never miss a deadline. Track your continuing education requirements across all your Georgia professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#ea580c', marginBottom: '0.5rem' }} aria-label="12 professions">12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#ea580c', marginBottom: '0.5rem' }} aria-label="Over 50 categories">50+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#ea580c', marginBottom: '0.5rem' }} aria-label="100 percent compliant">100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>State Board Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#ea580c', marginBottom: '0.5rem' }} aria-label="24/7 access">24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                Georgia CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#fed7aa',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ea580c' }}>
                        {prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'No CE Required'}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : prof.cycleYears === 4 ? 'Every 4 Years' : `Every ${prof.cycleYears} Years`}
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#fb923c', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Your Georgia License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Managing multiple Georgia professional licenses doesn't have to be complicated. 
                CEC Track automatically tracks your requirements, calculates your progress, and sends 
                timely reminders so you never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Automated deadline tracking for all GA professions',
                  'Upload certificates and match to mandatory topics',
                  'Real-time progress reports by category',
                  'Multi-state support for professionals licensed in multiple states',
                  'Secure cloud storage for all documents',
                  'Email notifications for upcoming deadlines'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#fb923c', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join hundreds of Georgia professionals who trust CEC Track to manage their continuing education.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #ea580c 0%, #fb923c 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Georgia State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources and contact information for Georgia professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Georgia Secretary of State
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Oversees Professional Licensing Boards Division including Architects, Engineers, CPAs, Nurses, Social Workers, Counselors, Therapists, and Appraisers.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+18447537825" aria-label="Call SOS at 844-753-7825">(844) 753-7825</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://sos.ga.gov/" target="_blank" rel="noopener noreferrer" aria-label="Visit SOS website">
                      sos.ga.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 237 Coliseum Drive, Macon, GA 31217
                  </div>
                  <a 
                    href="https://sos.ga.gov/how-to-guide/how-guide-professional-licensing-boards-division" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View all professional licensing boards"
                  >
                    View All Licensing Boards
                  </a>
                </div>
              </article>
            </div>

            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    State Bar of Georgia
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Administers Continuing Legal Education (CLE) requirements for attorneys licensed in Georgia. Mandatory annual CLE includes Ethics and Professionalism.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+14045278700" aria-label="Call State Bar at 404-527-8700">(404) 527-8700</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.gabar.org/" target="_blank" rel="noopener noreferrer" aria-label="Visit State Bar website">
                      www.gabar.org
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 104 Marietta Street NW, Suite 100, Atlanta, GA 30303
                  </div>
                  <a 
                    href="https://www.gabar.org/forthepublic/cle.cfm" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View CLE requirements"
                  >
                    View CLE Requirements
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="GA" />

      {/* CTA Section */}
      <section 
        aria-label="Call to action"
        style={{
          background: 'linear-gradient(135deg, #ea580c 0%, #fb923c 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your Georgia License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join professionals across Georgia who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
      </main>
    </>
  );
};

export default GALicenseRenewal;
